/**
 * src/pages/Admin.tsx — the `/admin` user-approval dashboard.
 *
 * Source: `D:/WCS/latex2html-french-trad/src/pages/Admin.tsx` (whole file, 99 lines).
 * Spec: `docs/spec/06-frontend-components.md` §11 (every state and both actions), §1.3/§1.4
 * (self-gating, not wrapped in `ProtectedRoute`), §15 D-19/D-20, §16 rank 18.
 *
 * Default export `AdminPage`, mounted by `App.tsx:22` as `<Route path="/admin" element={<AdminPage />} />`.
 * It is **not** wrapped in `ProtectedRoute` — it gates itself, and differently: an unauthenticated
 * visitor is bounced to `/` by the `<Navigate>` below and then meets `HexclaveAuthForm` there, so the login
 * form never renders at `/admin` (spec §1.4).
 *
 * Admin hooks moved: the source imported them from `@/hooks/use-admin`; in L2HC that file was merged
 * into `@/hooks/use-database` (see its header). Every key, endpoint, toast and invalidation is the
 * source's.
 *
 * ═══════════════════════════════════════════════════════════════════════════════════════════════
 * DF-22 rank 6 — THE BLANK-PAGE BUG LIVES ON LINE `format(new Date(user.created_at as string), …)`
 * ═══════════════════════════════════════════════════════════════════════════════════════════════
 * `date-fns/format` throws `RangeError: Invalid time value` on an unparseable date. `created_at` is
 * `string | null` on the wire (`src/lib/types.ts#User`), the source never guarded it
 * (`Admin.tsx:63`), and there is **no error boundary anywhere in the app** (spec §15 D-19, verified
 * absent in `src/`). A single malformed timestamp therefore unmounts the entire React tree and the
 * user sees a white page — the long-standing "blank page" report.
 *
 * The `as string` cast below exists **only** to satisfy `strictNullChecks`. It is deliberately a
 * cast and not a check: `??`, `if (…)`, `isValid()` or a try/catch would each be a FIX of DF-22
 * rank 6 and would have to be reverted (CLAUDE.md §3). Cast; do not check.
 *
 * ═══ DF-22 rank 18 — GLOBAL SPINNERS AND A NATIVE `confirm()` ════════════════════════════════
 *  - Both row buttons bind `disabled` and their `Loader2` spinner to the **shared** mutation state
 *    (`approveUser.isPending` / `rejectUser.isPending`, `Admin.tsx:70`, `:73`, `:83`, `:86`), so
 *    approving one user disables and spins Approve on **every** row at once (spec §15 D-20).
 *    Reproduced: do not track an in-flight user id.
 *  - Reject is guarded by a native blocking `confirm()` (`:79`), not the `AlertDialog` used
 *    everywhere else — and this is the DF-16 mass-destruction path: `documents.user_id`,
 *    `preambles.user_id` and `folders.user_id` all cascade, so rejecting a user destroys every
 *    document, preamble and folder they created, in a **shared** workspace. There is no count and no
 *    undo. Reproduced exactly; do not upgrade the dialog and do not add a count.
 *  - The mount gate is a bare `Loader2` with no label (`:15`).
 *
 * ═══ THE ONE ERROR STATE IN THE WHOLE APP ════════════════════════════════════════════════════
 * `'Failed to load users'` (`Admin.tsx:50`) is the **only** place any component reads a query's
 * error (spec §11, §3). It shows the literal string, never the server's message — `apiRequest`
 * discards the `{ error }` body (DF-22 rank 5) — and offers no Retry. It stays the only one: do not
 * generalise it into the sidebar.
 *
 * ═══ MECHANICAL DEVIATIONS, NEITHER OBSERVABLE ═══════════════════════════════════════════════
 *  1. The source's unused imports are dropped — `React` (`:2`, unused under the automatic JSX
 *     runtime) and `UserX` from lucide (`:5`, never rendered). L2HC compiles with
 *     `noUnusedLocals: true`.
 *  2. The map callback parameter is still named `user`, shadowing the `user` from `useAuth`
 *     (`Admin.tsx:10` vs `:57`), exactly as the source wrote it. Spec §11 suggests renaming it in
 *     the port; the source wins and the shadowing is not observable either way.
 */
import { useState } from 'react'
import {
  usePendingUsers,
  useApproveUser,
  useRejectUser,
  useAllUsers,
  useSetUserRole,
  useDeleteUser,
  useUpdateUserProfile,
} from '@/hooks/use-database'
import { format } from 'date-fns'
import {
  ShieldCheck,
  Check,
  X,
  Loader2,
  Shield,
  ShieldOff,
  Users,
  Pencil,
  Trash2,
} from 'lucide-react'
import { useAuth } from '@/contexts/AuthContext'
import { Navigate } from 'react-router-dom'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Button } from '@/components/ui/button'
import type { User } from '@/lib/types'

const AdminPage = () => {
  const { user, isLoading: authLoading } = useAuth()
  const { data: pendingUsers, isLoading, error } = usePendingUsers()
  const approveUser = useApproveUser()
  const rejectUser = useRejectUser()
  // NEW section's data — see the `<TeamSection/>` note below. Kept in the same component because
  // `Admin.tsx` self-gates on `user.role` and both sections sit behind that one check.
  const { data: allUsers, isLoading: usersLoading, error: usersError } = useAllUsers()
  const setUserRole = useSetUserRole()
  const deleteUser = useDeleteUser()
  const updateProfile = useUpdateUserProfile()

  /**
   * Which member has a dialog open. `null` closes it.
   *
   * Held as the whole row rather than an id so the dialogs can render the name and address they are
   * about even while the list is refetching underneath them — the roster is invalidated on every
   * mutation, and looking the row up by id mid-flight can briefly find nothing.
   */
  const [editing, setEditing] = useState<User | null>(null)
  const [deleting, setDeleting] = useState<User | null>(null)
  const [editName, setEditName] = useState('')
  const [editEmail, setEditEmail] = useState('')

  const openEditor = (member: User) => {
    setEditing(member)
    setEditName(member.username)
    setEditEmail(member.email)
  }

  // `Admin.tsx:15` — DF-22 rank 18: a bare, unlabelled full-block spinner.
  if (authLoading) return <div className="flex justify-center p-8"><Loader2 className="animate-spin h-8 w-8" /></div>

  // Protect route — `Admin.tsx:17-20`
  if (!user || user.role !== 'admin') {
    return <Navigate to="/" replace />
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      <div className="flex items-center gap-3 mb-8">
        <ShieldCheck className="w-10 h-10 text-primary" />
        <h1 className="text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-primary to-purple-600">
          Admin Dashboard
        </h1>
      </div>

      <div className="bg-card rounded-xl border shadow-sm overflow-hidden">
        <div className="p-6 border-b bg-muted/30">
          <h2 className="text-xl font-semibold flex items-center gap-2">
            Pending Approvals
            {pendingUsers && pendingUsers.length > 0 && (
              <span className="bg-yellow-500/20 text-yellow-600 text-xs px-2 py-1 rounded-full">
                {pendingUsers.length}
              </span>
            )}
          </h2>
          <p className="text-muted-foreground mt-1 text-sm">
            Review and approve new user registrations.
          </p>
        </div>

        <div className="divide-y">
          {isLoading ? (
            <div className="p-8 text-center text-muted-foreground">Loading...</div>
          ) : error ? (
            // The app's only error state — `Admin.tsx:50`. Literal text, no server message, no Retry.
            <div className="p-8 text-center text-red-500">Failed to load users</div>
          ) : !pendingUsers?.length ? (
            <div className="p-12 text-center text-muted-foreground flex flex-col items-center gap-3">
              <ShieldCheck className="w-12 h-12 opacity-20" />
              <p>No pending approvals found. All caught up!</p>
            </div>
          ) : (
            pendingUsers.map((user) => (
              <div key={user.id} className="p-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 hover:bg-muted/50 transition-colors">
                <div>
                  <div className="font-semibold text-lg">{user.username}</div>
                  <div className="text-muted-foreground text-sm">{user.email}</div>
                  <div className="text-xs text-muted-foreground mt-1">
                    {/* DF-22 rank 6 — UNGUARDED. `date-fns` throws on a bad date and, with no error
                        boundary anywhere, the whole app goes white. The cast is for the compiler
                        only; a null check or `??` fallback would be a fix. Do not add one. */}
                    Registered: {format(new Date(user.created_at as string), 'PPP p')}
                  </div>
                </div>

                <div className="flex items-center gap-3 w-full sm:w-auto">
                  <button
                    onClick={() => approveUser.mutate(user.id)}
                    /* DF-22 rank 18 — shared mutation state: this disables Approve on EVERY row. */
                    disabled={approveUser.isPending}
                    className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-md text-sm font-medium transition-colors"
                  >
                    {approveUser.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                    Approve
                  </button>

                  <button
                    onClick={() => {
                      // DF-22 rank 18 / DF-16 — native blocking confirm, no affected-item count,
                      // although this cascades away every document, preamble and folder the user
                      // created. Reproduced exactly; do not upgrade to an AlertDialog.
                      if (confirm('Are you sure you want to reject and delete this user?')) {
                        rejectUser.mutate(user.id)
                      }
                    }}
                    /* DF-22 rank 18 — shared mutation state: this disables Reject on EVERY row. */
                    disabled={rejectUser.isPending}
                    className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-4 py-2 bg-destructive/10 hover:bg-destructive/20 text-destructive rounded-md text-sm font-medium transition-colors border border-destructive/20"
                  >
                    {rejectUser.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <X className="w-4 h-4" />}
                    Reject
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/*
        ═══ NEW SECTION — Team. Owner request, 2026-08-09. Not in the source. ══════════════════
        Deliberately a SEPARATE block appended below, rather than columns added to the list above.
        That list is the ported "Pending Approvals" screen and is still under DF-22 visual
        validation (CLAUDE.md §3): its shared-spinner defect (rank 18), its unguarded date (rank 6)
        and its native confirm() are all reproduced on purpose. Editing it to add a role control
        would entangle a new feature with the defects being validated, which is the DF-21 failure
        mode. Everything new lives here; nothing above changed.

        It also cannot reuse that list even if it wanted to: `/pending-users` filters
        `WHERE is_approved = 0`, so approved people — the ones whose role you would change — are
        invisible to it. Hence the separate `useAllUsers` query.
      */}
      <div className="bg-card rounded-xl border shadow-sm overflow-hidden mt-8">
        <div className="p-6 border-b bg-muted/30">
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <Users className="w-5 h-5" />
            Team
            {allUsers && allUsers.length > 0 && (
              <span className="bg-primary/10 text-primary text-xs px-2 py-1 rounded-full">
                {allUsers.length}
              </span>
            )}
          </h2>
          <p className="text-muted-foreground mt-1 text-sm">
            Everyone with an account. Administrators can approve users and manage this list.
          </p>
        </div>

        <div className="divide-y">
          {usersLoading ? (
            <div className="p-8 text-center text-muted-foreground">Loading...</div>
          ) : usersError ? (
            <div className="p-8 text-center text-red-500">Failed to load users</div>
          ) : !allUsers?.length ? (
            <div className="p-8 text-center text-muted-foreground">No users yet.</div>
          ) : (
            allUsers.map((member) => {
              const isAdmin = member.role === 'admin'
              const isSelf = member.id === user.id
              // Mirrors the Worker's guards so the button explains itself rather than waiting for a
              // 400. The server still enforces all three — this only avoids offering a doomed click.
              const adminCount = allUsers.filter((u) => u.role === 'admin').length
              const isLastAdmin = isAdmin && adminCount <= 1
              // The main administrator — the address set in `wrangler.toml`. The client cannot read
              // that variable, so the **server** flags it on the roster rather than the UI guessing.
              const isMainAdmin = member.is_main_admin === true
              const blocked = isAdmin && (isMainAdmin || isLastAdmin || isSelf)

              // The server also refuses to edit a pre-Hexclave row (`external_id IS NULL`), but the
              // UI does not model that: those four rows were deleted on 2026-08-09 and every row
              // created since is provisioned with an external id, so the case cannot arise. The
              // server guard stays as defence; `readErrorBody` would surface it if it ever fired.
              const editBlocked = isMainAdmin && !isSelf

              // Delete refuses on all three, and unlike demotion it refuses for the main admin even
              // to themselves — losing that account cannot be undone from inside the app.
              const deleteBlocked = isMainAdmin || isSelf || isLastAdmin

              return (
                <div
                  key={member.id}
                  className="p-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 hover:bg-muted/50 transition-colors"
                >
                  <div className="min-w-0">
                    <div className="font-semibold text-lg flex items-center gap-2 flex-wrap">
                      <span className="truncate">{member.username}</span>
                      {isAdmin && (
                        <span className="bg-primary/10 text-primary text-xs px-2 py-0.5 rounded-full font-medium">
                          {isMainAdmin ? 'Main admin' : 'Admin'}
                        </span>
                      )}
                      {!member.is_approved && (
                        <span className="bg-yellow-500/20 text-yellow-600 text-xs px-2 py-0.5 rounded-full font-medium">
                          Pending
                        </span>
                      )}
                      {isSelf && <span className="text-xs text-muted-foreground">(you)</span>}
                    </div>
                    <div className="text-muted-foreground text-sm truncate">{member.email}</div>
                  </div>

                  <div className="flex items-center gap-3 w-full sm:w-auto shrink-0">
                    <button
                      onClick={() =>
                        setUserRole.mutate({ userId: member.id, role: isAdmin ? 'user' : 'admin' })
                      }
                      disabled={blocked || setUserRole.isPending}
                      title={
                        isMainAdmin
                          ? 'The main administrator is set in the deployment config and cannot be changed here'
                          : isLastAdmin
                            ? 'There must be at least one administrator'
                            : isSelf && isAdmin
                              ? 'You cannot remove your own admin access'
                              : undefined
                      }
                      className={
                        isAdmin
                          ? 'flex-1 sm:flex-none flex items-center justify-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors border border-border hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed'
                          : 'flex-1 sm:flex-none flex items-center justify-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-40 disabled:cursor-not-allowed'
                      }
                    >
                      {setUserRole.isPending ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : isAdmin ? (
                        <ShieldOff className="w-4 h-4" />
                      ) : (
                        <Shield className="w-4 h-4" />
                      )}
                      {isAdmin ? 'Remove admin' : 'Make admin'}
                    </button>

                    {/*
                      Edit — `PATCH /users/:id/profile`. Writes Hexclave first, then mirrors into D1.
                      Refused by the server for a legacy row (no Hexclave account behind it) and for
                      anyone but its owner on the main admin, so both are disabled here too.
                    */}
                    <button
                      onClick={() => openEditor(member)}
                      disabled={editBlocked || updateProfile.isPending}
                      title={
                        editBlocked
                          ? 'The main administrator can only be edited by its owner'
                          : undefined
                      }
                      className="flex items-center justify-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors border border-border hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      <Pencil className="w-4 h-4" />
                      <span className="sr-only sm:not-sr-only">Edit</span>
                    </button>

                    {/*
                      Delete — irreversible, and destroys the Hexclave account as well as the row.
                      Their documents survive (migration 0002). Confirmed by an AlertDialog rather
                      than the native confirm() the ported screen above uses: this block is new and
                      not under DF-22 validation, so it uses the app's own dialog.
                    */}
                    <button
                      onClick={() => setDeleting(member)}
                      disabled={deleteBlocked || deleteUser.isPending}
                      title={
                        isMainAdmin
                          ? 'The main administrator cannot be deleted'
                          : isSelf
                            ? 'You cannot delete your own account'
                            : isLastAdmin
                              ? 'There must be at least one administrator'
                              : undefined
                      }
                      className="flex items-center justify-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors border border-destructive/40 text-destructive hover:bg-destructive/10 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      {deleteUser.isPending && deleting?.id === member.id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Trash2 className="w-4 h-4" />
                      )}
                      <span className="sr-only sm:not-sr-only">Delete</span>
                    </button>
                  </div>
                </div>
              )
            })
          )}
        </div>
      </div>

      {/*
        ═══ Edit profile — PATCH /users/:id/profile ═══════════════════════════════════════════════
        The endpoint has existed since 2026-08-09 with nothing calling it. This is that caller.

        Only `username` and `email` are offered. `role` and `is_approved` are D1's own and have their
        own controls above; `password` and `email_verified` are deliberately not exposed by the
        Worker at all — see `worker/lib/hexclave-admin.ts`, "FIELDS DELIBERATELY NOT EXPOSED".
      */}
      <Dialog open={editing !== null} onOpenChange={(open) => !open && setEditing(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit profile</DialogTitle>
            <DialogDescription>
              Changes are saved to Hexclave first, then mirrored here. If Hexclave refuses — a
              duplicate or malformed address — nothing changes on either side.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="edit-name">Name</Label>
              <Input
                id="edit-name"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                autoComplete="off"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-email">Email</Label>
              <Input
                id="edit-email"
                type="email"
                value={editEmail}
                onChange={(e) => setEditEmail(e.target.value)}
                autoComplete="off"
              />
              <p className="text-xs text-muted-foreground">
                Changing the address marks it unverified at Hexclave until they confirm it.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>
              Cancel
            </Button>
            <Button
              onClick={() => {
                if (!editing) return
                updateProfile.mutate(
                  { userId: editing.id, username: editName.trim(), email: editEmail.trim() },
                  // Closed only on success, so a refusal leaves the dialog open with the values
                  // still in it and the reason in a toast — rather than dismissing and losing both.
                  { onSuccess: () => setEditing(null) },
                )
              }}
              disabled={
                updateProfile.isPending || editName.trim() === '' || editEmail.trim() === ''
              }
            >
              {updateProfile.isPending && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/*
        ═══ Delete — the most destructive button in the app ═══════════════════════════════════════
        It removes the Hexclave account as well as the row, so it ends the person's ability to sign
        in rather than merely revoking their access here. There is no undo.

        The wording states what survives as well as what goes: documents are NOT deleted (migration
        0002 / DF-16), and saying so is the difference between an admin hesitating over the right
        thing and hesitating over the wrong one.
      */}
      <AlertDialog open={deleting !== null} onOpenChange={(open) => !open && setDeleting(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {deleting?.username}?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2">
                <p>
                  This deletes their account — their password, any Google sign-in, and their access
                  to this app. They will be signed out immediately.
                </p>
                <p>
                  Their documents, folders and preambles are <strong>kept</strong>. Only the account
                  is removed.
                </p>
                <p>
                  They can sign up again with the same email address, but it will be a new account:
                  they would set up their sign-in again and <strong>you would have to approve
                  them</strong>.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => {
                if (!deleting) return
                deleteUser.mutate(deleting.id, { onSettled: () => setDeleting(null) })
              }}
            >
              Delete account
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}

export default AdminPage
